import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { Spinner } from 'components';
import { SortableItem, SortableList, Trigger } from 'components/Sortable';
import Tooltip from 'components/Tooltip';
import {
  EditMemberModal,
  RemoveMemberModal,
  InviteMembersBanner,
} from 'components/Committee';
import { useNotification } from 'hooks/useNotification';
import {
  CommitteeResponse,
  CommitteeData,
  CommitteeAttributes,
} from 'lib/models/committee';
import { API_ROUTES } from 'lib/api-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { MemberCard } from '../MemberCard/MemberCard.component';

import styles from './CommitteeMembers.module.scss';

export const CommitteeMembers: React.FC = memo(() => {
  const [t] = useTranslation('committee');
  const { user } = useAuth();
  const notificationInstance = useNotification();

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [membersList, setMembersList] = useState<Array<CommitteeData>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragged, setIsDragged] = useState<boolean>(false);
  const [draggedIndex, setdraggedIndex] = useState<number>(0);
  const [userData, setUserData] = useState<CommitteeAttributes>({
    id: '',
    avatar_url: '',
    name: '',
    first_name: '',
    last_name: '',
    role: '',
    position: 0,
  });

  const getMembers = async () => {
    setIsLoading(true);
    const { data: cardsResponse } = await apiInstance.get<CommitteeResponse>(
      API_ROUTES.COMMITTEE
    );
    const sortedMembers = cardsResponse.data.sort(function (a, b) {
      return a.attributes.position - b.attributes.position;
    });
    setMembersList(sortedMembers);
    setIsLoading(false);
  };

  useEffect(() => {
    getMembers();
  }, []);

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const onRemoveMember = async () => {
    try {
      const deleteMember = await apiInstance.delete(
        `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`
      );
      if (deleteMember) {
        notificationInstance.success({
          message: `${userData.name} ${t(
            'remove-member-modal.notification-message'
          )}`,
        });
      }
      const modifiedList = membersList.filter(
        (item) => item.attributes !== userData
      );
      setMembersList([...modifiedList]);
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const onEditmember = async (
    updatedData: CommitteeAttributes,
    profileImage: string
  ) => {
    try {
      if (profileImage) {
        const payload = {
          manager: {
            avatar: { file: profileImage.split(',')[1] },
          },
        };
        await apiInstance.put(
          `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`,
          payload
        );
      }
      if (
        updatedData.name != userData.name ||
        updatedData.role != userData.role
      ) {
        const payload = {
          manager: {
            first_name: updatedData.first_name,
            last_name: updatedData.last_name,
            name: updatedData.name,
            role: updatedData.role,
          },
        };
        await apiInstance.put(
          `${API_ROUTES.SOCIETY_MANAGERS}/${userData.id}`,
          payload
        );
      }

      if (
        profileImage ||
        updatedData.name != userData.name ||
        updatedData.role != userData.role
      ) {
        notificationInstance.success({
          message: `${updatedData.name} ${t(
            'edit-member-modal.notification-message'
          )}`,
        });
      }

      const modifiedList = membersList;
      const userIndex = membersList.findIndex((item) => item.id == userData.id);
      modifiedList[userIndex].attributes = updatedData;
      if (profileImage)
        modifiedList[userIndex].attributes.avatar_url = profileImage;
      setMembersList([...modifiedList]);
    } catch (error) {
      notificationInstance.handleExceptionError(error);
    }
  };

  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(true);
  const onDragEnd = async ({
    oldIndex: oldPosition,
    newIndex: newPosition,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    setIsTooltipVisible(true);
    setIsDragged(false);
    if (membersList && oldPosition !== newPosition) {
      const member = membersList[oldPosition];

      const oldMembersList = structuredClone(membersList);
      const tempMembersList = structuredClone(membersList);

      if (oldPosition > newPosition) {
        tempMembersList.splice(newPosition, 0, tempMembersList[oldPosition]);
        tempMembersList.splice(oldPosition + 1, 1);
      } else {
        tempMembersList.splice(
          newPosition + 1,
          0,
          tempMembersList[oldPosition]
        );
        tempMembersList.splice(oldPosition, 1);
      }

      const previousMemberPosition =
        tempMembersList[newPosition - 1]?.attributes.position;
      tempMembersList[newPosition].attributes.position = previousMemberPosition
        ? previousMemberPosition + 1
        : 1;
      for (
        let index = newPosition + 1;
        index < tempMembersList.length;
        index++
      ) {
        tempMembersList[index].attributes.position += 1;
      }
      setMembersList([...tempMembersList]);
      if (member) {
        const payload = {
          manager: {
            position: tempMembersList[newPosition].attributes.position,
          },
        };
        const response = await apiInstance.put(
          `${API_ROUTES.SOCIETY_MANAGERS}/${member.id}`,
          payload
        );
        if (!response) {
          setMembersList([...oldMembersList]);
        }
      }
    }
  };

  const Card = (userData: CommitteeAttributes) => {
    const isUser = user?.userable_id == userData.id;
    return (
      <MemberCard
        userData={userData}
        isUser={isUser}
        onEditClick={() => {
          setUserData(userData);
          setIsEditModalOpen(true);
        }}
        onRemoveClick={() => {
          setUserData(userData);
          setIsRemoveModalOpen(true);
        }}
      />
    );
  };

  return (
    <div
      className={cn(styles.container, {
        [styles.dragging]: isDragged,
      })}
    >
      {isLoading ? (
        <Spinner wrapper size="small" />
      ) : (
        <>
          {membersList && membersList.length < 2 ? (
            <>
              <InviteMembersBanner showButton={true} />
              {membersList.map((user) => {
                return Card(user.attributes);
              })}
            </>
          ) : (
            <div className={styles.arrangeContainer}>
              <SortableList
                axis="xy"
                lockAxis="xy"
                lockToContainerEdges={true}
                useDragHandle={true}
                updateBeforeSortStart={(ele) => {
                  setIsTooltipVisible(false);
                  setIsDragged(true);
                  setdraggedIndex(ele.index);
                }}
                onSortEnd={onDragEnd}
              >
                {membersList.map((user, index) => {
                  return (
                    <SortableItem
                      key={user.id}
                      index={index}
                      className={styles.arrangeCard}
                    >
                      <Trigger
                        className={cn(styles.drag, {
                          [styles.dragged]: isDragged && draggedIndex === index,
                        })}
                      >
                        {Card(user.attributes)}
                        <div className={styles.tooltipContainer}>
                          <Tooltip
                            content={t('drag.tooltip-content')}
                            color="dark"
                            isV2Design
                            showTippy
                            contentPositionClassName={styles.tooltip}
                            className={styles['tooltip-content']}
                            disabled={!isTooltipVisible}
                          >
                            <div className={styles.overlay1}></div>
                            <div className={styles.overlay2}></div>
                          </Tooltip>
                        </div>
                      </Trigger>
                    </SortableItem>
                  );
                })}
              </SortableList>
            </div>
          )}

          {isEditModalOpen && (
            <EditMemberModal
              isOpen={isEditModalOpen}
              onClose={closeEditModal}
              userData={userData}
              onSave={(updatedData, profileImage) =>
                onEditmember(updatedData, profileImage)
              }
            />
          )}

          {isRemoveModalOpen && (
            <RemoveMemberModal
              isOpen={isRemoveModalOpen}
              onClose={closeRemoveModal}
              userName={userData.name}
              onRemove={onRemoveMember}
            />
          )}
        </>
      )}
    </div>
  );
});

CommitteeMembers.displayName = 'CommitteeMembers';
